import { envPublic } from '@/libs/config/env.public';
import type { FirebaseEnv } from '@/libs/firebase/init';
import { getCurrentFirebaseEnv, useFirebaseEnvStore } from '@/stores/firebase-env-store';
import { useMemo } from 'react';
import { useShallow } from 'zustand/shallow';

/**
 * Per-env URLs that travel with the selected Firebase project. Only the
 * `production` set is hardcoded here; `development` reads from `.env` (the
 * same source the build already uses), so dev devs don't have to keep this
 * file in sync.
 *
 * Public values — safe to ship in the JS bundle.
 */
const PRODUCTION_URLS = {
  firebaseCloudFunctionUrl: 'https://uskg-gcwa37moma-uc.a.run.app',
  pictureUrl:
    'https://firebasestorage.googleapis.com/v0/b/uskg-prod.appspot.com/o/profile-pictures%2f',
  pinUrl: 'https://firebasestorage.googleapis.com/v0/b/uskg-prod.appspot.com/o/pins%2f',
} as const;

export type EnvUrls = {
  firebaseCloudFunctionUrl: string;
  pictureUrl: string;
  pinUrl: string;
};

export const getEnvUrls = (env: FirebaseEnv): EnvUrls => {
  if (env === 'production') return { ...PRODUCTION_URLS };
  return {
    firebaseCloudFunctionUrl: envPublic.firebaseCloudFunctionUrl,
    pictureUrl: envPublic.pictureUrl,
    pinUrl: envPublic.pinUrl,
  };
};

/**
 * Non-hook accessor — useful inside axios interceptors and helpers running
 * outside React. Reads the latest selected env from the store on every call,
 * so toggling env at runtime is picked up immediately.
 */
export const getCurrentEnvUrls = (): EnvUrls => getEnvUrls(getCurrentFirebaseEnv());

/**
 * React hook variant — re-renders when the selected env changes.
 */
export const useEnvUrls = (): EnvUrls => {
  const env = useFirebaseEnvStore(useShallow(s => s.env));
  return useMemo(() => getEnvUrls(env), [env]);
};
